import { CheckCircleOutline, Edit } from '@mui/icons-material';
import { Box, Theme, Typography, useTheme } from '@mui/material';

import { ChapterState } from '@/types';

export const EpisodeChapterStatus = ({
  chapterState,
  chapterRemainingDuration,
  isChapterVerifiable,
  isChapterActive,
}: {
  chapterState: ChapterState;
  chapterRemainingDuration: number;
  isChapterVerifiable: boolean;
  isChapterActive: boolean;
}) => {
  const theme = useTheme();
  const statusContent = getStatusContents({
    chapterState,
    chapterRemainingDuration,
    isChapterVerifiable,
    theme,
  });
  const showPill =
    chapterState === 'verified' ||
    chapterState === 'completed' ||
    chapterState === 'quiz_ready';

  return showPill ? (
    <StatusPill statusContent={statusContent} />
  ) : (
    <Typography
      variant="body3"
      sx={{
        color: isChapterActive
          ? theme.palette.chapter.activeText
          : statusContent.color,
      }}>
      {statusContent.text}
    </Typography>
  );
};

const PILL_HEIGHT = 26;
const StatusPill = ({
  statusContent,
}: {
  statusContent: ReturnType<typeof getStatusContents>;
}) => {
  const Icon = statusContent.icon;

  return (
    <Box
      sx={{
        background: statusContent.background,
        display: 'flex',
        height: PILL_HEIGHT,
        borderRadius: PILL_HEIGHT / 2,
        flexDirection: 'row',
        px: 1.5,
        width: 'fit-content',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {!!Icon && (
          <Icon
            sx={{
              width: 14,
              height: 14,
              mr: 0.5,
              color: statusContent.color,
            }}
          />
        )}
        <Typography
          sx={{ color: statusContent.color, textWrap: 'nowrap' }}
          variant="body3SemiBold">
          {statusContent.text}
        </Typography>
      </Box>
    </Box>
  );
};

const getStatusContents = ({
  chapterState,
  chapterRemainingDuration,
  isChapterVerifiable,
  theme,
}: {
  chapterState: ChapterState;
  chapterRemainingDuration: number;
  isChapterVerifiable: boolean;
  theme: Theme;
}) => {
  switch (chapterState) {
    case 'completed':
    case 'verified':
      return {
        text: isChapterVerifiable ? 'Quiz completed' : 'Completed',
        color: theme.palette.chapter.contrastText,
        icon: CheckCircleOutline,
        background: theme.palette.chapter.completed,
      };
    case 'quiz_ready':
      return {
        text: 'Start Quiz',
        color: theme.palette.chapter.contrastText,
        background: theme.palette.chapter.quizReady,
        icon: Edit,
      };
    case 'on_going':
      return {
        text: `${chapterRemainingDuration} min${chapterRemainingDuration === 1 ? '' : 's'} left`,
        color: theme.palette.chapter.text,
      };
    default:
      return {
        text: `${chapterRemainingDuration} mins`,
        color: theme.palette.chapter.text,
      };
  }
};
